import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Phone, Check, ArrowRight, Shield, Palette, Lock } from "lucide-react";
import axios from "axios";
import SEO, { LOCATION_KEYWORDS } from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DoorsPage = () => {
  const [doorProjects, setDoorProjects] = useState([]);

  const fetchDoorProjects = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/projects?category=doors`);
      setDoorProjects(response.data);
    } catch {
      // Silently handle error
    }
  }, []);

  useEffect(() => {
    fetchDoorProjects();
  }, [fetchDoorProjects]);

  const doorTypes = [
    {
      title: "Composite Doors",
      description: "Premium quality composite doors combining style with security. Available in a wide range of colours and finishes.",
      image: "https://static.wixstatic.com/media/4dd2ec_fcc9b0bd02b94c1c8a911eb9fd5e5d82~mv2.jpg/v1/fill/w_600,h_800,al_c,q_85,enc_avif,quality_auto/545884792_24533040359658803_8282707251856425224_n.jpg",
      features: ["Thermally efficient", "Multi-point locking", "Weather resistant", "10 year warranty"],
    },
    {
      title: "uPVC Doors",
      description: "Cost-effective uPVC doors that don't compromise on quality or security. Low maintenance and long-lasting.",
      image: "https://static.wixstatic.com/media/4dd2ec_20fa493bdb294df6a402c08c6c75abd3~mv2.jpg/v1/fill/w_600,h_800,al_c,q_85,enc_avif,quality_auto/530275505_1288230266432371_8244233030664850202_n.jpg",
      features: ["Energy efficient", "Secure locking", "Easy to maintain", "Various colours"],
    },
    {
      title: "French Doors",
      description: "Elegant French doors that open up your home to the garden, flooding rooms with natural light.",
      image: "https://customer-assets.emergentagent.com/job_orange-glaze/artifacts/ych1nl7d_french%20doors.jpg",
      features: ["Double opening", "Security glass", "Multiple styles", "Custom sizes"],
    },
    {
      title: "Patio Doors",
      description: "Sliding patio doors for seamless indoor-outdoor living with maximum glass area.",
      image: "https://customer-assets.emergentagent.com/job_orange-glaze/artifacts/2oize85x_patio%20doors.jpg",
      features: ["Space saving", "Smooth operation", "Secure locks", "Energy rated"],
    },
  ];

  const colours = [
    { name: "Anthracite Grey", hex: "#383E42" },
    { name: "Black", hex: "#1A1A1A" },
    { name: "White", hex: "#FFFFFF" },
    { name: "Light Oak", hex: "#C4A35A" },
    { name: "Rosewood", hex: "#65000B" },
    { name: "Irish Oak", hex: "#8B6914" },
    { name: "Chartwell Green", hex: "#7D9867" },
    { name: "Blue", hex: "#1E3A8A" },
  ];

  return (
    <div data-testid="doors-page" className="pt-32 lg:pt-36">
      <SEO 
        title="Composite Doors & uPVC Doors Lanarkshire | Front Doors Motherwell, Glasgow"
        description="Composite doors, uPVC doors, French doors & patio doors in Motherwell, Hamilton, Glasgow & Lanarkshire. Multi-point locking, wide colour range. 10 year guarantee. Free quotes - call 07917 181597"
        keywords={`composite doors, uPVC doors, French doors, patio doors, front doors, back doors, external doors, door installation, composite doors Lanarkshire, composite doors Glasgow, composite doors Motherwell, doors Hamilton, anthracite grey doors, black composite doors, ${LOCATION_KEYWORDS}`}
        canonical="/doors"
      />
      {/* Hero Section */}
      <section className="relative py-24 bg-[#0A0A0A]">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{
            backgroundImage: `url('https://static.wixstatic.com/media/4dd2ec_d37f7d12c7ee44b49c9895a734f537a0~mv2.jpg/v1/fill/w_609,h_922,al_c,q_85,enc_avif,quality_auto/4dd2ec_d37f7d12c7ee44b49c9895a734f537a0~mv2.jpg')`,
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <h1
              className="text-4xl sm:text-5xl font-black text-white mb-6"
              style={{ fontFamily: "'Chivo', sans-serif" }}
            >
              EXTERNAL{" "}
              <span className="text-[#FF6B00]">DOORS</span>
            </h1>
            <p className="text-gray-300 text-lg mb-8">
              We have a huge choice of bespoke external doors from uPVC to Composite in a
              range of beautiful colours. All our external doors come with multi-point
              locking systems and a 10 year guarantee. Pair with our <Link to="/windows" className="text-[#FF6B00] hover:underline">A-rated double glazing windows</Link> for a complete home upgrade.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                data-testid="doors-cta"
                className="bg-[#FF6B00] text-white font-bold uppercase tracking-wide px-8 py-4 hover:bg-[#E65000] transition-all text-center"
              >
                Get Free Quote
              </Link>
              <a
                href="tel:07917181597"
                className="bg-transparent border-2 border-white text-white font-bold uppercase tracking-wide px-8 py-4 hover:bg-white hover:text-[#0A0A0A] transition-all flex items-center justify-center gap-2"
              >
                <Phone className="w-5 h-5" />
                07917 181597
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Features Bar */}
      <section className="bg-[#F5F5F5] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Shield,
                title: "10 Year Warranty",
                description: "Full peace of mind on all our doors",
              },
              {
                icon: Lock,
                title: "Multi-Point Locking",
                description: "Maximum security for your home",
              },
              {
                icon: Palette,
                title: "Range of Colours",
                description: "Customise to match your style",
              },
            ].map((feature) => (
              <div
                key={feature.title}
                data-testid={`door-feature-${feature.title.toLowerCase().replace(/\s+/g, '-')}`}
                className="flex items-center space-x-4"
              >
                <div className="w-14 h-14 bg-[#FF6B00] flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-[#0A0A0A] uppercase tracking-wide">
                    {feature.title}
                  </h3>
                  <p className="text-[#525252] text-sm">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Door Types Section */}
      <section data-testid="door-types-section" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2
              className="text-3xl sm:text-4xl font-black text-[#0A0A0A] mb-4"
              style={{ fontFamily: "'Chivo', sans-serif" }}
            >
              DOOR <span className="text-[#FF6B00]">STYLES</span>
            </h2>
            <p className="text-[#525252] max-w-2xl mx-auto">
              Choose from our extensive range of external door styles to find the perfect
              entrance for your home. All installed with matching <Link to="/windows" className="text-[#FF6B00] hover:underline">double glazed windows</Link> available.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {doorTypes.map((door) => (
              <div
                key={door.title}
                data-testid={`door-type-${door.title.toLowerCase().replace(/\s+/g, '-')}`}
                className="bg-[#F5F5F5] border border-[#E5E5E5] overflow-hidden group hover:border-[#FF6B00] transition-all flex flex-col md:flex-row"
              >
                <div className="md:w-2/5 aspect-[3/4] md:aspect-auto overflow-hidden">
                  <img
                    src={door.image}
                    alt={door.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="md:w-3/5 p-6 flex flex-col justify-center">
                  <h3
                    className="text-xl font-black text-[#0A0A0A] mb-3"
                    style={{ fontFamily: "'Chivo', sans-serif" }}
                  >
                    {door.title}
                  </h3>
                  <p className="text-[#525252] text-sm mb-4">{door.description}</p>
                  <ul className="space-y-2">
                    {door.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-sm">
                        <Check className="w-4 h-4 text-[#FF6B00]" />
                        <span className="text-[#0A0A0A]">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Colour Options */}
      <section className="py-20 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2
              className="text-3xl sm:text-4xl font-black text-[#0A0A0A] mb-4"
              style={{ fontFamily: "'Chivo', sans-serif" }}
            >
              COLOUR <span className="text-[#FF6B00]">OPTIONS</span>
            </h2>
            <p className="text-[#525252] max-w-2xl mx-auto">
              Express your style with our wide range of door colours. From classic
              whites to bold anthracite grey, we have options to suit every home.
            </p>
          </div>

          <div className="grid grid-cols-4 md:grid-cols-8 gap-4">
            {colours.map((colour) => (
              <div
                key={colour.name}
                data-testid={`colour-${colour.name.toLowerCase().replace(/\s+/g, '-')}`}
                className="text-center group"
              >
                <div
                  className="w-full aspect-square border-2 border-[#E5E5E5] mb-2 group-hover:border-[#FF6B00] transition-colors"
                  style={{ backgroundColor: colour.hex }}
                />
                <p className="text-xs text-[#525252] font-medium">{colour.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Door Work */}
      {doorProjects.length > 0 && (
        <section data-testid="recent-door-work" className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center mb-12">
              <h2
                className="text-3xl font-black text-[#0A0A0A]"
                style={{ fontFamily: "'Chivo', sans-serif" }}
              >
                RECENT <span className="text-[#FF6B00]">DOOR INSTALLATIONS</span>
              </h2>
              <Link
                to="/recent-work"
                className="text-[#FF6B00] font-bold uppercase text-sm tracking-wide flex items-center gap-2 hover:gap-3 transition-all"
              >
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {doorProjects.slice(0, 4).map((project, index) => (
                <div
                  key={project.id}
                  data-testid={`door-project-${index}`}
                  className="group bg-white border border-[#E5E5E5] overflow-hidden hover:border-[#FF6B00] transition-all"
                >
                  <div className="aspect-[3/4] overflow-hidden">
                    <img
                      src={project.image_url}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-[#0A0A0A] text-sm">
                      {project.title}
                    </h3>
                    {project.location && (
                      <p className="text-xs text-[#525252]">{project.location}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="bg-[#FF6B00] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-3xl font-black text-white mb-4"
            style={{ fontFamily: "'Chivo', sans-serif" }}
          >
            FIND YOUR PERFECT DOOR
          </h2>
          <p className="text-white/90 mb-8 max-w-2xl mx-auto">
            <Link to="/contact" className="text-white underline hover:text-[#0A0A0A]">Contact us</Link> today for a free quote on external composite doors, uPVC doors, or
            any of our door range. We also install <Link to="/windows" className="text-white underline hover:text-[#0A0A0A]">double glazing windows</Link> and <Link to="/outbuilds" className="text-white underline hover:text-[#0A0A0A]">garden rooms</Link> across Lanarkshire, Glasgow, and surrounding areas. See our <Link to="/reviews" className="text-white underline hover:text-[#0A0A0A]">customer reviews</Link>.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-white text-[#0A0A0A] font-bold uppercase tracking-wide px-8 py-4 hover:bg-[#0A0A0A] hover:text-white transition-all"
          >
            Request Quote
          </Link>
        </div>
      </section>

      {/* Comprehensive Door Information */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2
                className="text-2xl sm:text-3xl font-black text-[#0A0A0A] mb-6"
                style={{ fontFamily: "'Chivo', sans-serif" }}
              >
                COMPOSITE VS UPVC <span className="text-[#FF6B00]">DOORS</span>
              </h2>
              <p className="text-[#525252] mb-4">
                Choosing between composite and uPVC doors is one of the most common questions we receive from homeowners in Lanarkshire 
                and Glasgow. Both options offer excellent security, energy efficiency, and durability, but they have distinct characteristics 
                that may make one more suitable for your home than the other.
              </p>
              <h3 className="font-bold text-[#0A0A0A] mt-6 mb-3">Composite Doors</h3>
              <p className="text-[#525252] mb-4">
                Composite doors are crafted from a combination of materials including GRP (glass reinforced plastic), wood, insulating foam, 
                and uPVC. This construction makes them incredibly strong, secure, and thermally efficient. They don't warp, crack, or fade 
                like traditional timber doors, and they require virtually no maintenance beyond occasional cleaning.
              </p>
              <p className="text-[#525252] mb-4">
                The GRP skin gives composite doors their authentic wood-grain texture and appearance, making them an excellent choice for 
                period properties or homeowners who prefer a traditional aesthetic. They're available in an extensive range of colours and 
                styles, from classic Victorian designs to contemporary minimalist options.
              </p>
              <h3 className="font-bold text-[#0A0A0A] mt-6 mb-3">uPVC Doors</h3>
              <p className="text-[#525252]">
                uPVC doors offer excellent value for money without compromising on quality or security. They're completely weatherproof, 
                won't rot or rust, and maintain their appearance for decades with minimal upkeep. Modern uPVC doors feature multi-chambered 
                profiles for improved insulation and come with the same multi-point locking systems as composite doors.
              </p>
            </div>
            <div>
              <h3
                className="text-xl font-black text-[#0A0A0A] mb-4"
                style={{ fontFamily: "'Chivo', sans-serif" }}
              >
                DOOR SECURITY FEATURES
              </h3>
              <p className="text-[#525252] mb-4">
                Security is paramount when choosing a new external door. All doors we supply and fit come with advanced security features 
                as standard, providing peace of mind for you and your family.
              </p>
              <div className="bg-[#F5F5F5] p-6 mb-6">
                <h4 className="font-bold text-[#0A0A0A] mb-3">Security Features Included:</h4>
                <ul className="space-y-2 text-[#525252]">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[#FF6B00] mt-1 flex-shrink-0" />
                    <span><strong>Multi-point locking:</strong> 3-5 point locking systems engage at multiple points around the door frame</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[#FF6B00] mt-1 flex-shrink-0" />
                    <span><strong>Anti-snap cylinders:</strong> Prevent lock snapping, one of the most common burglary methods</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[#FF6B00] mt-1 flex-shrink-0" />
                    <span><strong>Reinforced frames:</strong> Steel reinforced hinges and frames resist forced entry</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[#FF6B00] mt-1 flex-shrink-0" />
                    <span><strong>Laminated glass:</strong> Optional toughened and laminated glass for glass panels</span>
                  </li>
                </ul>
              </div>
              <h4 className="font-bold text-[#0A0A0A] mb-3">Popular Door Colours in Lanarkshire</h4>
              <p className="text-[#525252] mb-4">
                Anthracite grey has become the most requested door colour in recent years, offering a modern, sophisticated look that 
                complements both traditional and contemporary homes. Other popular choices include black, chartwell green, and various 
                woodgrain finishes like Irish Oak and Rosewood.
              </p>
              <p className="text-[#525252]">
                We offer over 20 standard colours plus the option for RAL colour matching to achieve your perfect shade. Side panels, 
                fanlights, and glazing options allow for complete customisation to suit your property's style and your personal taste.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Door Installation Areas */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-2xl sm:text-3xl font-black text-white mb-6"
            style={{ fontFamily: "'Chivo', sans-serif" }}
          >
            DOOR INSTALLATION ACROSS <span className="text-[#FF6B00]">LANARKSHIRE</span>
          </h2>
          <p className="text-gray-300 max-w-3xl mx-auto mb-8">
            We install composite doors, uPVC doors, French doors, and patio doors throughout Lanarkshire and Glasgow. 
            Our experienced fitters ensure every door is installed to the highest standards, with proper alignment, 
            sealing, and security features correctly configured.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
            {[
              { name: "Motherwell", link: "/double-glazing-motherwell" },
              { name: "Hamilton", link: "/double-glazing-hamilton" },
              { name: "East Kilbride", link: "/double-glazing-east-kilbride" },
              { name: "Wishaw", link: "/double-glazing-wishaw" },
              { name: "Airdrie", link: "/double-glazing-airdrie" },
              { name: "Coatbridge", link: "/double-glazing-coatbridge" },
            ].map((area) => (
              <Link 
                key={area.name}
                to={area.link}
                className="bg-[#1A1A1A] p-4 text-center hover:bg-[#FF6B00] transition-colors"
              >
                <span className="text-white font-medium">{area.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Related Services Links */}
      <section className="py-12 bg-[#F5F5F5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="font-bold text-[#0A0A0A] mb-6">Related Services</h3>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/windows" className="px-6 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors">
              Double Glazing Windows
            </Link>
            <Link to="/outbuilds" className="px-6 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors">
              Garden Rooms
            </Link>
            <Link to="/recent-work" className="px-6 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors">
              View Our Work
            </Link>
            <Link to="/faq" className="px-6 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors">
              Door Installation FAQ
            </Link>
            <Link to="/reviews" className="px-6 py-3 bg-white border border-[#E5E5E5] hover:border-[#FF6B00] transition-colors">
              Customer Reviews
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DoorsPage;
